<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import axios from 'axios'
import { AlertCircle, CheckCircle2, Send } from 'lucide-vue-next'
import AltchaField from '@/Components/Forms/AltchaField.vue'
import PhoneInput from '@/Components/Forms/PhoneInput.vue'
import { Button } from '@/Components/ui/button'

/**
 * A question to the team, from the help panel or from a tutor's profile.
 *
 * The two used to be separate forms with their own look, wording and errors,
 * and drifted apart. Both post to the same place, so they are one form; the
 * only difference is which tutor, if any, the question is about.
 */
const props = withDefaults(defineProps<{
  idPrefix: string
  tutorSlug?: string
  messagePlaceholder?: string
}>(), {
  tutorSlug: undefined,
  messagePlaceholder: 'Describe your question…',
})

const emit = defineEmits<{ close: [] }>()

const MESSAGE_MIN = 10
const MESSAGE_MAX = 2000

type Field = 'name' | 'email' | 'phone' | 'message' | 'altcha'

const page = usePage()
const isGuest = computed(() => !page.props.auth?.user)
// Asked only where there is none on file; the server falls back to the account's.
const asksForPhone = computed(() => !page.props.auth?.has_phone)

const name = ref('')
const email = ref('')
const phone = ref('')
const message = ref('')
const altcha = ref('')
const altchaField = ref<InstanceType<typeof AltchaField> | null>(null)
const phoneField = ref<InstanceType<typeof PhoneInput> | null>(null)

const sending = ref(false)
const sent = ref(false)
const errors = ref<Partial<Record<Field, string>>>({})
const failure = ref('')

// The phone field says so itself as the number is typed; this only avoids saying it twice.
const phoneLooksWrong = computed(() => Boolean(phone.value) && phoneField.value?.isValid() === false)

const messageLength = computed(() => message.value.trim().length)
const charactersToGo = computed(() => Math.max(0, MESSAGE_MIN - messageLength.value))

const inputClass = (field: Field) => [
  'h-11 w-full rounded-md border bg-surface px-3 text-[15px] text-ink placeholder:text-subtle focus:outline-none focus:ring-3',
  errors.value[field] ? 'border-danger focus:ring-danger/20' : 'border-line-strong focus:border-brand focus:ring-brand/20',
]

const id = (field: Field) => `${props.idPrefix}-${field}`

function check(): Partial<Record<Field, string>> {
  const found: Partial<Record<Field, string>> = {}

  if (isGuest.value && !name.value.trim()) found.name = 'Please tell us your name.'
  if (isGuest.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) found.email = 'Please enter a valid email address.'
  if (asksForPhone.value && phone.value && !phoneField.value?.isValid()) found.phone = 'Please check the number, or leave it empty.'
  if (messageLength.value < MESSAGE_MIN) found.message = `Please write at least ${MESSAGE_MIN} characters.`

  return found
}

// A failed send puts the caret where the problem is.
async function focusFirstError() {
  await nextTick()
  const first = (['name', 'email', 'phone', 'message'] as Field[]).find((field) => errors.value[field])

  if (first) document.getElementById(id(first))?.focus()
}

async function submit() {
  if (sending.value) return

  errors.value = check()
  failure.value = ''

  if (Object.keys(errors.value).length) {
    focusFirstError()

    return
  }

  sending.value = true

  if (isGuest.value && !(await altchaField.value?.ensure())) {
    failure.value = 'We could not check this browser. Please try again.'
    sending.value = false

    return
  }

  try {
    await axios.post('/support', {
      message: message.value.trim(),
      ...(props.tutorSlug ? { tutor: props.tutorSlug } : {}),
      ...(asksForPhone.value && phone.value ? { phone: phone.value } : {}),
      ...(isGuest.value ? { altcha: altcha.value, name: name.value.trim(), email: email.value.trim() } : {}),
    })
    sent.value = true
  } catch (error: any) {
    if (error.response?.status === 422) {
      const server = error.response.data.errors ?? {}
      errors.value = Object.fromEntries(Object.entries(server).map(([field, list]) => [field, (list as string[])[0]]))
      focusFirstError()
    } else if (error.response?.status === 429) {
      failure.value = 'Too many messages sent. Please wait a moment and try again.'
    } else {
      failure.value = 'Something went wrong. Please try again.'
    }
  } finally {
    sending.value = false
    altchaField.value?.reset()
  }
}

function clearError(field: Field) {
  if (errors.value[field]) errors.value = { ...errors.value, [field]: undefined }
}
</script>

<template>
  <div v-if="sent" class="py-6 text-center" role="status">
    <CheckCircle2 class="mx-auto size-10 text-success" />
    <h3 class="mt-3 text-lg text-ink">Message sent</h3>
    <p class="mt-2 text-sm text-muted">
      We have emailed you a copy and will reply within 24 hours.
    </p>
    <Button class="mt-6" variant="secondary" @click="emit('close')">Close</Button>
  </div>

  <form v-else class="space-y-4" novalidate @submit.prevent="submit">
    <template v-if="isGuest">
      <div>
        <label class="mb-1.5 block text-sm font-semibold text-ink" :for="id('name')">Your name</label>
        <input
          :id="id('name')"
          v-model="name"
          type="text"
          autocomplete="name"
          :aria-invalid="Boolean(errors.name)"
          :aria-describedby="errors.name ? `${id('name')}-error` : undefined"
          :class="inputClass('name')"
          @input="clearError('name')"
        />
        <p v-if="errors.name" :id="`${id('name')}-error`" class="mt-1 text-sm text-danger">{{ errors.name }}</p>
      </div>
      <div>
        <label class="mb-1.5 block text-sm font-semibold text-ink" :for="id('email')">Your email</label>
        <input
          :id="id('email')"
          v-model="email"
          type="email"
          autocomplete="email"
          :aria-invalid="Boolean(errors.email)"
          :aria-describedby="errors.email ? `${id('email')}-error` : undefined"
          :class="inputClass('email')"
          @input="clearError('email')"
        />
        <p v-if="errors.email" :id="`${id('email')}-error`" class="mt-1 text-sm text-danger">{{ errors.email }}</p>
      </div>
    </template>

    <div v-if="asksForPhone">
      <label class="mb-1.5 block text-sm font-semibold text-ink" :for="id('phone')">
        Mobile <span class="font-normal text-muted">(optional)</span>
      </label>
      <PhoneInput
        ref="phoneField"
        v-model="phone"
        size="compact"
        :input-id="id('phone')"
        :invalid="Boolean(errors.phone)"
        @update:model-value="clearError('phone')"
      />
      <p v-if="errors.phone && !phoneLooksWrong" class="mt-1 text-sm text-danger">{{ errors.phone }}</p>
      <p v-else-if="!phoneLooksWrong" class="mt-1 text-sm text-muted">If you'd like us to reply on WhatsApp too.</p>
    </div>

    <div>
      <label class="mb-1.5 block text-sm font-semibold text-ink" :for="id('message')">How can we help?</label>
      <textarea
        :id="id('message')"
        v-model="message"
        rows="4"
        :maxlength="MESSAGE_MAX"
        :placeholder="messagePlaceholder"
        :aria-invalid="Boolean(errors.message)"
        :aria-describedby="`${id('message')}-hint`"
        :class="['block w-full resize-none rounded-md border bg-surface p-3 text-[15px] text-ink placeholder:text-subtle focus:outline-none focus:ring-3', errors.message ? 'border-danger focus:ring-danger/20' : 'border-line-strong focus:border-brand focus:ring-brand/20']"
        @input="clearError('message')"
        @keydown.meta.enter="submit"
        @keydown.ctrl.enter="submit"
      ></textarea>
      <p :id="`${id('message')}-hint`" :class="['mt-1 text-sm', errors.message ? 'text-danger' : 'text-muted']">
        <template v-if="errors.message">{{ errors.message }}</template>
        <template v-else-if="charactersToGo > 0">{{ charactersToGo }} more {{ charactersToGo === 1 ? 'character' : 'characters' }} to go</template>
        <template v-else>{{ messageLength }} / {{ MESSAGE_MAX }}</template>
      </p>
    </div>

    <div v-if="isGuest">
      <AltchaField ref="altchaField" v-model="altcha" />
      <p v-if="errors.altcha" class="text-sm text-danger">{{ errors.altcha }}</p>
    </div>

    <p v-if="failure" class="flex items-start gap-2 text-sm text-danger" role="alert">
      <AlertCircle class="mt-0.5 size-4 shrink-0" /> {{ failure }}
    </p>

    <Button type="submit" class="w-full" :disabled="sending">
      <Send class="size-4" />
      {{ sending ? 'Sending…' : 'Send message' }}
    </Button>

    <p v-if="isGuest" class="text-center text-sm text-muted">We'll reply to the email address you give.</p>
  </form>
</template>
